import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Pagination,
    Breadcrumbs,
    Link as MuiLink,
    IconButton
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getFailedConsignments } from '../../services/deliverySheetApi';
import { printConsignment } from '../../services/consignmentApi';
import { ConsignmentView } from '../Consignments';

interface FailedConsignment {
    id: number;
    consignment_no: string;
    destinationCity?: { name: string };
    service_type: string;
    booking_type: string;
    cod_amount: number;
    total_price: number;
    created_at: string;
    status: { name: string; badge: string };
}

const FailedConsignments = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [consignments, setConsignments] = useState<FailedConsignment[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedConsignmentId, setSelectedConsignmentId] = useState<number | null>(null);

    useEffect(() => {
        fetchFailedConsignments();
    }, [page]);

    const fetchFailedConsignments = async () => {
        try {
            setLoading(true);
            const response = await getFailedConsignments(page, 20);
            setConsignments(response.data.data);
            setTotalPages(response.data.last_page || 1);
            setTotalCount(response.data.total || 0);
        } catch (error: any) {
            console.error('Error fetching failed consignments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewConsignment = (consignmentId: number) => {
        setSelectedConsignmentId(consignmentId);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedConsignmentId(null);
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/delivery-sheets')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Delivery Sheets
                </MuiLink>
                <Typography color="text.primary">Failed Consignments</Typography>
            </Breadcrumbs>

            <Card>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                        Failed Consignments ({totalCount})
                    </Typography>

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Consignment #</TableCell>
                                    <TableCell>Destination</TableCell>
                                    <TableCell>Service Type</TableCell>
                                    <TableCell>Booking Date</TableCell>
                                    <TableCell>Booking Type</TableCell>
                                    <TableCell>Total Amount / COD Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">Loading...</TableCell>
                                    </TableRow>
                                ) : consignments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    consignments.map((consignment) => (
                                        <TableRow key={consignment.id}>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => handleViewConsignment(consignment.id)}
                                                    sx={{ textDecoration: 'none', cursor: 'pointer' }}
                                                >
                                                    {consignment.consignment_no}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>{consignment.destinationCity?.name || '-'}</TableCell>
                                            <TableCell>{consignment.service_type}</TableCell>
                                            <TableCell>{new Date(consignment.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell>{consignment.booking_type}</TableCell>
                                            <TableCell>
                                                {consignment.booking_type === 'RETAIL' 
                                                    ? consignment.total_price.toFixed(2)
                                                    : consignment.cod_amount.toFixed(2)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={consignment.status.name}
                                                    color="error"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton 
                                                    size="small" 
                                                    color="info"
                                                    onClick={() => printConsignment(consignment.id)}
                                                >
                                                    <PrintIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Consignment Details Modal */}
            <ConsignmentView
                open={modalOpen}
                onClose={handleCloseModal}
                consignmentId={selectedConsignmentId}
            />
        </Box>
    );
};

export default FailedConsignments;

